import React from "react";

import { useGetPlanCostsQuery } from "EmployeeApplication/api";
import arrayMutators from "final-form-arrays";
import { Field, Form, FormRenderProps } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import formatCurrency from "Utils/formatCurrency";
import { LifePlan, Plan } from "Utils/types";
import { validateRequired } from "Utils/Validations";

import BodyText from "Cobalt/BodyText";
import Header from "Cobalt/Header";
import LoadingBar from "Cobalt/LoadingBar";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalProps } from "Cobalt/Modal";
import { Radio } from "Cobalt/RadioGroup";
import { FooterTD, FooterTR, HeaderTR, Table, TD, TH, TR } from "Cobalt/TableV2";
import CarrierLogo from "Common/CarrierLogo";
import { SaveButton } from "Common/forms/buttons";
import { Span } from "Common/HTML";

const useCostBreakdown = ({ values, plan, employeePolicyID }) => {
  values.dependents.some((dependent) => dependent.enrolling !== null);
  const currentEnrollingDependentIDs = values.dependents.filter((dependent) => dependent.enrolling === "Yes").map((dependent) => dependent.dependentID);
  const { isFetching: isFetchingPlanCosts, data: { planCosts = [] } = {} } = useGetPlanCostsQuery({
    dependentIDs: currentEnrollingDependentIDs,
    id: employeePolicyID,
    type: plan.type,
  });
  const breakdown = (planCosts.find((cost) => cost.planID === plan.id) || {})?.costBreakdowns?.age || {};
  return {
    breakdown,
    isFetchingPlanCosts,
  };
};

interface ModalContentProps extends FormRenderProps {
  employeePolicyID: string;
  plan: Plan | LifePlan;
  title: string;
}

const dependentValidation = (value, allValues) => {
  const isEmployeeEnrolling = allValues.employeeEnrolling === "Yes";
  return isEmployeeEnrolling ? validateRequired(value) : undefined;
};

const ModalContent = ({ employeePolicyID, title, plan, handleSubmit, values, submitting, touched, errors }: ModalContentProps) => {
  const anydependentHasSelection = values.dependents.some((dependent) => dependent.enrolling !== null);
  const isEmployeeDeclining = values.employeeEnrolling === "No";
  const isEmployeeEnrolling = values.employeeEnrolling === "Yes";
  const { breakdown, isFetchingPlanCosts } = useCostBreakdown({ values, plan, employeePolicyID });
  return (
    <form onSubmit={handleSubmit}>
      <ModalHeader closeButton>
        <Header level={6} mb={0}>
          {title}
        </Header>
      </ModalHeader>
      <ModalBody>
        <BodyText className="flex items-end" mb={4}>
          <CarrierLogo plan={plan} maxSize={3} className="flex-grow" mr={3} />
          <span className="flex-grow">{plan.display_name}</span>
        </BodyText>
        <Table>
          <thead>
            <HeaderTR>
              <TH>Name</TH>
              <TH width="35%">Enroll In Coverage</TH>
              <TH width="60px">Monthly Cost</TH>
            </HeaderTR>
          </thead>
          <tbody>
            <TR highlightFirstCell={false} withBorder={false}>
              <TD>
                {values.employeeFormattedName}
                {touched?.employeeEnrolling && errors?.employeeEnrolling && (
                  <Span ml={4} className="text-error-900">
                    {errors.employeeEnrolling}
                  </Span>
                )}
              </TD>
              <TD>
                <Field name="employeeEnrolling" type="radio" value="Yes" key="Yes" validate={validateRequired}>
                  {({ input }) => (
                    <Radio name={input.name} onChange={input.onChange} value={input.value} checked={input.checked} inline>
                      Yes
                    </Radio>
                  )}
                </Field>
                <Field name="employeeEnrolling" type="radio" value="No" key="No" validate={validateRequired}>
                  {({ input }) => (
                    <Radio name={input.name} onChange={input.onChange} value={input.value} checked={input.checked} inline>
                      No
                    </Radio>
                  )}
                </Field>
              </TD>
              <TD>
                {isFetchingPlanCosts ? (
                  <LoadingBar ariaText="Loading Employee Costs" />
                ) : (
                  <span className={isEmployeeDeclining ? "text-gray-500" : ""}>{formatCurrency(breakdown.employeeCost)}</span>
                )}
              </TD>
            </TR>
            {values.dependents.length > 0 && (
              <TR highlightFirstCell={false} withBorder={false}>
                <TD colSpan={99} align="left">
                  Family Members:
                  {isEmployeeDeclining && <Span ml={4}>(If you decline coverage, you cannot enroll your family members.)</Span>}
                </TD>
              </TR>
            )}
            <FieldArray name="dependents">
              {({ fields }) =>
                fields.map((name, index) => {
                  const { formattedName } = fields.value[index];
                  const fieldName = `${name}.enrolling`;
                  const errorMsg = errors?.dependents?.[index]?.enrolling;
                  return (
                    <TR indented highlightFirstCell={false} key={index} withBorder={false}>
                      <TD>
                        {formattedName}
                        {touched[fieldName] && errorMsg && (
                          <Span ml={4} className="text-error-900">
                            {errorMsg}
                          </Span>
                        )}
                      </TD>
                      <TD>
                        <Field name={fieldName} type="radio" value="Yes" key="Yes" validate={dependentValidation}>
                          {({ input }) => (
                            <Radio
                              name={input.name}
                              onChange={input.onChange}
                              value={input.value}
                              checked={input.checked}
                              disabled={isEmployeeDeclining}
                              inline
                            >
                              Yes
                            </Radio>
                          )}
                        </Field>
                        <Field name={fieldName} type="radio" value="No" key="No" validate={dependentValidation}>
                          {({ input }) => (
                            <Radio
                              name={input.name}
                              onChange={input.onChange}
                              value={input.value}
                              checked={input.checked}
                              disabled={isEmployeeDeclining}
                              inline
                            >
                              No
                            </Radio>
                          )}
                        </Field>
                      </TD>
                      <TD>&nbsp;</TD>
                    </TR>
                  );
                })
              }
            </FieldArray>
            {values.dependents.length > 0 && (
              <TR highlightFirstCell={false}>
                <TD colSpan={2}>Family Members Total</TD>
                <TD>
                  {!anydependentHasSelection && <>&nbsp;</>}
                  {anydependentHasSelection && isFetchingPlanCosts && <LoadingBar ariaText="Loading Dependents Costs" />}
                  {anydependentHasSelection && !isFetchingPlanCosts && (
                    <span className={isEmployeeDeclining ? "text-gray-500" : ""}>{formatCurrency(breakdown.dependentCost)}</span>
                  )}
                </TD>
              </TR>
            )}
          </tbody>
          <tfoot>
            <FooterTR>
              <FooterTD colSpan={2}>Total Monthly Cost</FooterTD>
              <FooterTD>
                {!isEmployeeEnrolling && !isEmployeeDeclining && <>&nbsp;</>}
                {isEmployeeDeclining && "$0"}
                {isEmployeeEnrolling && isFetchingPlanCosts && <LoadingBar ariaText="Loading Total Costs" />}
                {isEmployeeEnrolling && !isFetchingPlanCosts && formatCurrency(breakdown.employeeTotalCost)}
              </FooterTD>
            </FooterTR>
          </tfoot>
        </Table>
      </ModalBody>
      <ModalFooter>
        <SaveButton submitting={submitting} />
      </ModalFooter>
    </form>
  );
};

export interface AgeEnrollmentFormSubmitValues {
  employeeEnrolling: "Yes" | "No" | null | undefined;
  dependents: Array<{
    dependentID: string;
    enrolling: "Yes" | "No" | null | undefined;
  }>;
}

export interface AgeEnrollmentFormValueProps {
  employeeEnrolling: "Yes" | "No" | null | undefined;
  employeeFormattedName: string;
  dependents: Array<{
    dependentID: string;
    enrolling: "Yes" | "No" | null | undefined;
    formattedName: string;
    dependentType: "spouse" | "domestic_partner" | "child";
  }>;
}

interface AgeEnrollmentModalProps extends ModalProps {
  employeePolicyID: string;
  plan: Plan | LifePlan;
  title: string;
  initialValues: AgeEnrollmentFormValueProps;
  onSubmit: (values: AgeEnrollmentFormSubmitValues) => void;
}

const AgeEnrollmentModal = ({ employeePolicyID, plan, title, initialValues, onSubmit, ...rest }: AgeEnrollmentModalProps) => {
  const sanitizedOnSubmit = ({ employeeEnrolling, dependents }) =>
    onSubmit({
      employeeEnrolling,
      dependents: dependents.map(({ dependentID, enrolling }) => ({ dependentID, enrolling: employeeEnrolling === "No" ? "No" : enrolling })),
    });
  return (
    <Modal {...rest}>
      <Form
        initialValues={initialValues}
        onSubmit={sanitizedOnSubmit}
        mutators={{
          ...arrayMutators,
        }}
      >
        {(formRenderProps) => <ModalContent employeePolicyID={employeePolicyID} plan={plan} title={title} {...formRenderProps} />}
      </Form>
    </Modal>
  );
};

export default AgeEnrollmentModal;
